import { useState } from 'react';
import { Link } from 'react-router-dom';
import { leadsApi } from '../utils/api';

export default function ForCreatives() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    creativeType: '',
    portfolio: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await leadsApi.submit({ source: 'creatives', ...formData });
      setSubmitted(true);
    } catch (error) {
      // Show the failure and keep the form filled in - never confirm an
      // application the server did not store (B8).
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-700 via-purple-800 to-indigo-900 text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-9xl">🎨</div>
          <div className="absolute bottom-10 right-10 text-9xl">✨</div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-9xl">🎮</div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
              <span className="mr-2">🎨</span>
              For Creatives
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Turn Your Creativity Into <span className="text-purple-300">Impact & Income</span>
            </h1>
            
            <p className="text-xl text-purple-100 mb-8 leading-relaxed">
              Join our creator network. Design characters, craft stories, and build games that help 
              children find their voice—while earning ongoing royalties from your work.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#apply"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 hover:bg-accent-600 text-white font-bold text-lg rounded-xl transition-all shadow-lg"
              >
                Apply to Join
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold text-lg rounded-xl border-2 border-white/30 transition-all"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What We're Looking For */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              We're Looking For Creative Talent
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you write stories, create art, or design games—there's a place for you 
              in our mission to help children through play.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Story Writers */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mb-6">
                <span className="text-3xl">📝</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Story Writers
              </h3>
              <p className="text-gray-600 mb-4">
                Create engaging characters, narratives, and dialogue that children love. 
                Your stories become the foundation for interactive speech adventures.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  Character development & backstories
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  Interactive dialogue scripts
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">✓</span>
                  Story arcs & adventures
                </li>
              </ul>
            </div>

            {/* Artists & Designers */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 border border-pink-100">
              <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mb-6">
                <span className="text-3xl">🎨</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Artists & Designers
              </h3>
              <p className="text-gray-600 mb-4">
                Bring characters and worlds to life with your visual creativity. 
                Design artwork that captures children's imagination and keeps them engaged.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  Character & mascot design
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  Game environments & backgrounds
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">✓</span>
                  UI/UX & animation assets
                </li>
              </ul>
            </div>

            {/* Game Designers */}
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 border border-green-100">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mb-6">
                <span className="text-3xl">🎮</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Game Designers
              </h3>
              <p className="text-gray-600 mb-4">
                Design gameplay mechanics that make speech practice feel like pure fun. 
                Create levels, challenges, and reward systems that keep kids coming back.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  Gameplay mechanics & loops
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  Level design & progression
                </li>
                <li className="flex items-start">
                  <span className="text-teal-500 mr-2">✓</span>
                  Reward & engagement systems
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How Our Creator Program Works
            </h2>
            <p className="text-xl text-gray-600">
              A simple path from your creativity to helping kids—and earning income
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Apply & Get Approved</h3>
              <p className="text-gray-600 text-sm">
                Submit your portfolio and tell us about your creative vision. We review all applications personally.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Create & Collaborate</h3>
              <p className="text-gray-600 text-sm">
                Work with our team to align your creative work with speech therapy goals. We provide guidance and resources.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="font-bold text-gray-900 mb-2">License Your Work</h3>
              <p className="text-gray-600 text-sm">
                Your approved content gets licensed to SpeechFormation. You retain ownership while we distribute it.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                4
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Earn Royalties</h3>
              <p className="text-gray-600 text-sm">
                Receive ongoing revenue share based on how your content performs. The more kids it helps, the more you earn.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Share Model */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Earn While Making a Difference
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                Our revenue share model is inspired by platforms like Netflix. When your content 
                is used in our games, you earn ongoing royalties—not just a one-time payment.
              </p>
              
              <div className="space-y-4">
                {[
                  'Competitive revenue share on all licensed content',
                  'Ongoing royalties based on content usage & performance',
                  'Transparent reporting dashboard to track your earnings',
                  'Quarterly payments with no hidden fees',
                  'Retain ownership of your original creative work',
                ].map((point, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-300">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 rounded-3xl p-8 backdrop-blur border border-white/10">
              <div className="text-center">
                <div className="text-6xl mb-4">💰</div>
                <h3 className="text-2xl font-bold mb-4">Revenue Share Model</h3>
                <p className="text-gray-300 mb-6">
                  Similar to how Netflix licenses content from creators, we offer competitive 
                  revenue sharing that rewards your creativity long-term.
                </p>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-purple-300 font-semibold">
                    Your creativity + Our platform = Helping kids + Your income
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Mission */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Your Art Can Change Lives
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 sm:p-12 border border-purple-100">
            <div className="text-center">
              <div className="text-5xl mb-6">💜</div>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Every character you design, every story you write, every game mechanic you create—it 
                becomes a tool that helps a child practice speech without feeling like it's work.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Imagine a child who dreads therapy sessions suddenly <em>asking</em> to play with 
                the character you created. That's the impact your creativity can have.
              </p>
              <p className="text-lg text-purple-600 font-semibold">
                This isn't just freelance work. It's purpose-driven creativity with lasting impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What We Look For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-green-500 mr-2">✓</span> Must Have
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Portfolio demonstrating your creative work</li>
                <li>• Passion for helping children and families</li>
                <li>• Ability to create child-friendly content</li>
                <li>• Openness to feedback and collaboration</li>
                <li>• Commitment to quality and deadlines</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-purple-500 mr-2">★</span> Nice to Have
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Experience with educational content</li>
                <li>• Understanding of child development</li>
                <li>• Background in game design or interactive media</li>
                <li>• Experience with speech therapy concepts</li>
                <li>• Animation or interactive design skills</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Apply to Join Our Creator Network
            </h2>
            <p className="text-xl text-gray-600">
              Tell us about yourself and your creative work
            </p>
          </div>

          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
              <div className="text-5xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Received!</h3>
              <p className="text-gray-600">
                Thank you for your interest in joining our creator network. We'll review your 
                application and get back to you within 5-7 business days.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Creative Specialty *
                  </label>
                  <select
                    required
                    value={formData.creativeType}
                    onChange={(e) => setFormData({...formData, creativeType: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                  >
                    <option value="">Select your specialty</option>
                    <option value="story-writer">Story Writer / Narrative Designer</option>
                    <option value="artist">Artist / Graphic Designer</option>
                    <option value="animator">Animator / Motion Designer</option>
                    <option value="game-designer">Game Designer</option>
                    <option value="multiple">Multiple Specialties</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Portfolio Link *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.portfolio}
                    onChange={(e) => setFormData({...formData, portfolio: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    placeholder="https://yourportfolio.com"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Behance, Dribbble, personal website, or any link showcasing your work
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tell Us About Yourself
                  </label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
                    placeholder="What draws you to this mission? What kind of content would you love to create?"
                  />
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border-2 border-red-200 bg-red-50 p-4"
                  >
                    <span className="text-xl leading-none" aria-hidden="true">⚠️</span>
                    <div>
                      <p className="font-bold text-red-800">We couldn't send your application</p>
                      <p className="text-red-700 text-sm mt-1">{submitError}</p>
                      <p className="text-red-700 text-sm mt-1">
                        Your answers are still here, so you can try again — or email us directly at{' '}
                        <a href="mailto:partners@speechformation.com" className="underline font-semibold">
                          partners@speechformation.com
                        </a>.
                      </p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg rounded-xl transition-all shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting…' : 'Submit Application'}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting, you agree to our{' '}
                  <Link to="/terms" className="text-purple-600 hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-purple-600 hover:underline">Privacy Policy</Link>
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "Do I need experience with speech therapy?",
                a: "No! We provide guidance on how to align your creative work with speech therapy goals. Your job is to be creative—we'll help with the therapeutic framework."
              },
              {
                q: "How much can I earn?",
                a: "Earnings vary based on the type of content, usage, and performance. Our revenue share model ensures you earn ongoing royalties as long as your content is being used."
              },
              {
                q: "Do I retain ownership of my work?",
                a: "Yes. You license your work to SpeechFormation for use in our platform, but you retain ownership of your original creative assets."
              },
              {
                q: "How long does the application process take?",
                a: "We review applications within 5-7 business days. If selected, we'll schedule a call to discuss collaboration opportunities."
              },
              {
                q: "Can I work on multiple projects?",
                a: "Absolutely! Many of our creators work on various projects across stories, art, and game design. The more you contribute, the more you can earn."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Create With Purpose?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Join our community of creators making a real difference in children's lives.
          </p>
          <a 
            href="#apply" 
            className="inline-flex items-center px-8 py-4 bg-white text-purple-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            Apply Now
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}
